import type { FC, PropsWithChildren } from 'react'

import styled from '@emotion/styled'

import Link from 'next/link'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'

import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronLeftIcon } from '@segment/matcha/components/Icons/Common'

import Typography from '@segment/matcha/components/Typography'
import Media from '@segment/matcha/components/Media'

import AccordionBase, * as Accordion from '@segment/matcha/recipes/Accordion'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import RichText from '@segment/matcha/recipes/RichText'

import type { FaqRichText as FaqProps } from '@segment/api/staticSiteBackend/dataHub'

import { useActiveState } from '@segment/utils/useActiveState'

const copy = {
  heading: `Frequently asked questions`,
}

const link = {
  text: `Back to Data Hub`,
  url: `/data-hub`,
}

interface FaqSectionProps {
  data: FaqProps[]
}

const FaqSection: FC<PropsWithChildren<FaqSectionProps>> = ({ data }) => {
  const items = new Array(data.length).fill(false)
  const { activeItems, toggle } = useActiveState(items, {
    allowMultipleActive: true,
  })

  return (
    <Wrapper>
      <StyledSectionBase variant="noCopy">
        <Section.Contents>
          <Heading variant="h2" tag="h3">
            {copy.heading}
          </Heading>
          <Accordion.JsonLdFaq
            data={data.map(({ answer, question }) => {
              return {
                question,
                answer: documentToHtmlString(answer),
              }
            })}
          />
          {activeItems.map((active, index) => (
            <AccordionBase key={`item-${index}`}>
              <Accordion.Header onClick={() => toggle(index)}>
                <Accordion.TitleWrapper>
                  <Accordion.Title opened={active}>{data[index].question}</Accordion.Title>
                </Accordion.TitleWrapper>
                <Accordion.Icon opened={active} />
              </Accordion.Header>
              <Accordion.Body>
                {active && (
                  <Accordion.ContentWrapper>
                    <RichText data={data[index].answer} />
                  </Accordion.ContentWrapper>
                )}
              </Accordion.Body>
            </AccordionBase>
          ))}
        </Section.Contents>
      </StyledSectionBase>
      <Media greaterThan="sm">
        <StyledBottomSectionBase variant="noCopy">
          <Section.Contents>
            <Link href={link.url}>
              <ButtonText tag="span" size="small" iconStart={ChevronLeftIcon}>
                {link.text}
              </ButtonText>
            </Link>
          </Section.Contents>
        </StyledBottomSectionBase>
      </Media>
    </Wrapper>
  )
}

const StyledBottomSectionBase = styled(SectionBase)`
  margin-top: -56px;
`

const Heading = styled(Typography)`
  padding-bottom: 16px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    padding-bottom: 24px;
  }

  ${({ theme }) => theme.breakpoints.up(`xl`)} {
    padding-bottom: 32px;
  }
`

const StyledSectionBase = styled(SectionBase)`
  max-width: 1004px;
  overflow-x: unset;

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    padding-bottom: 56px;
  }

  ${Section.Wrapper} {
    padding-top: 0;
  }
`

const Wrapper = styled.div`
  background: ${({ theme }) => theme.palette.neutrals.white[100]};
`

export default FaqSection
